import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Loader2,
  FileText,
  Send,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  ExternalLink,
  AlertCircle,
  Trash2,
  Pencil,
  Mail,
  X,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  Wrench,
  FileStack,
  ArrowRight,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "@/lib/utils";
import { useProposal, useDeleteProposal } from "../../hooks/useProposals";
import {
  useProposalSigningUrl,
  useSignatureRequest,
  useSendProposal,
} from "../../hooks/useSignatureTracking";
import { usePdfGeneration } from "../../hooks/usePdfGeneration";
import { ProposalWizard } from "../wizard/ProposalWizard";
import type {
  ProposalStatus,
  ProposalLineItem,
  PriceUnit,
  ProposalTemplate,
  BuilderMode,
  FixedPagePolicy,
} from "../../types";
import type { EditableProposalSeed } from "../../hooks/useProposalWizard";
import { PRICE_UNIT_LABELS } from "../../types";

interface Props {
  proposalId: string;
  /** Called before PDF generation — use to flush any unsaved canvas changes. */
  onBeforeRegenerate?: () => Promise<boolean>;
}

const STATUS_STEPS: {
  status: ProposalStatus;
  label: string;
  icon: typeof Send;
}[] = [
  { status: "draft", label: "Draft", icon: FileText },
  { status: "sent", label: "Sent", icon: Send },
  { status: "viewed", label: "Viewed", icon: Eye },
  { status: "signed", label: "Signed", icon: CheckCircle2 },
];

const STATUS_ORDER: Record<ProposalStatus, number> = {
  draft: 0,
  sent: 1,
  viewed: 2,
  signed: 3,
  declined: 2,
  expired: 2,
};

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description:
      "This proposal started from a simpler reusable template blueprint.",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description:
      "This proposal started from a template marked for the richer builder direction.",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-4 w-4" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description:
      "This proposal mainly relies on pages chosen directly by the template.",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description:
      "This proposal inherits fixed pages from Brand Setup.",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description:
      "This proposal inherits both template-chosen pages and brand-defined fixed pages.",
  },
};

function getPriceUnitLabel(priceUnit: PriceUnit | null | undefined) {
  if (!priceUnit) return null;
  return PRICE_UNIT_LABELS[priceUnit] ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getRelatedTemplateFromProposal(value: unknown): ProposalTemplate | null {
  if (!isRecord(value)) return null;

  const candidates = [
    value.template,
    value.proposal_template,
    value.proposal_templates,
  ];

  for (const candidate of candidates) {
    if (isRecord(candidate) && typeof candidate.id === "string") {
      return candidate as unknown as ProposalTemplate;
    }
  }

  return null;
}

function buildEditableProposalSeed(value: unknown): EditableProposalSeed | null {
  if (!isRecord(value) || typeof value.id !== "string") return null;

  const proposalLineItems = Array.isArray(value.proposal_line_items)
    ? value.proposal_line_items
        .filter(isRecord)
        .map((item) => ({
          id: typeof item.id === "string" ? item.id : undefined,
          service_item_id:
            typeof item.service_item_id === "string" ? item.service_item_id : null,
          name: typeof item.name === "string" ? item.name : "",
          description:
            typeof item.description === "string" ? item.description : null,
          price: typeof item.price === "number" ? item.price : null,
          price_unit:
            typeof item.price_unit === "string"
              ? (item.price_unit as PriceUnit)
              : null,
          frequency:
            typeof item.frequency === "string" ? item.frequency : null,
          duration_hours:
            typeof item.duration_hours === "number"
              ? item.duration_hours
              : null,
          sort_order:
            typeof item.sort_order === "number" ? item.sort_order : null,
        }))
    : [];

  return {
    id: value.id,
    client_id: typeof value.client_id === "string" ? value.client_id : "",
    template_id:
      typeof value.template_id === "string" ? value.template_id : null,
    prospect_name:
      typeof value.prospect_name === "string" ? value.prospect_name : "",
    prospect_email:
      typeof value.prospect_email === "string" ? value.prospect_email : null,
    prospect_address:
      typeof value.prospect_address === "string" ? value.prospect_address : null,
    prospect_city:
      typeof value.prospect_city === "string" ? value.prospect_city : null,
    prospect_state:
      typeof value.prospect_state === "string" ? value.prospect_state : null,
    prospect_zip:
      typeof value.prospect_zip === "string" ? value.prospect_zip : null,
    project_summary:
      typeof value.project_summary === "string" ? value.project_summary : null,
    ai_project_summary:
      typeof value.ai_project_summary === "string"
        ? value.ai_project_summary
        : null,
    notes: typeof value.notes === "string" ? value.notes : null,
    tax_rate: typeof value.tax_rate === "number" ? value.tax_rate : null,
    proposal_line_items: proposalLineItems,
  };
}

function getTemplateBuilderMode(
  template: ProposalTemplate | null | undefined
): BuilderMode {
  return template?.builder_mode === "drag_drop" ? "drag_drop" : "prebuilt";
}

function getTemplateFixedPagePolicy(
  template: ProposalTemplate | null | undefined
): FixedPagePolicy {
  if (
    template?.fixed_page_policy === "brand_defined" ||
    template?.fixed_page_policy === "mixed"
  ) {
    return template.fixed_page_policy;
  }

  return "template_defined";
}

function formatCurrencyValue(n: number | null | undefined) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n ?? 0);
}

export function ProposalDetail({ proposalId, onBeforeRegenerate }: Props) {
  const navigate = useNavigate();
  const { data: proposal, isLoading } = useProposal(proposalId);
  const { data: sigReq } = useSignatureRequest(proposalId);
  const { data: signingUrl } = useProposalSigningUrl(proposalId);
  const sendProposal = useSendProposal();
  const pdf = usePdfGeneration();
  const deleteProposal = useDeleteProposal();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSendPanel, setShowSendPanel] = useState(false);
  const [sendMessage, setSendMessage] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [lastSendResult, setLastSendResult] = useState<{ provider?: string; pdf_url?: string; email_sent?: boolean; email_skipped_reason?: string | null } | null>(null);

  const editableSeed = useMemo(
    () => buildEditableProposalSeed(proposal),
    [proposal]
  );

  const relatedTemplate = useMemo(
    () => getRelatedTemplateFromProposal(proposal),
    [proposal]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f0f0f]">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  if (!proposal || !editableSeed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#0f0f0f] text-white/40">
        <AlertCircle className="h-8 w-8" />
        <p>Proposal not found.</p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] text-white">
        <ProposalWizard
          mode="edit"
          initialProposal={editableSeed}
          initialTemplate={relatedTemplate}
          onComplete={() => {
            setIsEditing(false);
          }}
        />
      </div>
    );
  }

  const currentStepIdx = STATUS_ORDER[proposal.status] ?? 0;
  const isTerminal =
    proposal.status === "declined" || proposal.status === "expired";
  const lineItems = proposal.proposal_line_items ?? [];
  const canSend =
    proposal.status === "draft" &&
    !sendProposal.isPending &&
    !sigReq?.dropbox_request_id &&
    !proposal.dropbox_signature_request_id;
  const hasProspectEmail =
    typeof proposal.prospect_email === "string" &&
    proposal.prospect_email.trim().length > 0;
  const hasActiveSigningLink =
    (proposal.status === "sent" || proposal.status === "viewed") &&
    typeof signingUrl === "string" &&
    signingUrl.trim().length > 0;

  const builderMode = getTemplateBuilderMode(relatedTemplate);
  const builderCfg = BUILDER_MODE_CONFIG[builderMode];
  const fixedCfg =
    FIXED_PAGE_POLICY_CONFIG[getTemplateFixedPagePolicy(relatedTemplate)];
  const isBuilderTemplate = builderMode === "drag_drop";

  const relatedTemplateServiceCount =
    relatedTemplate?.template_services?.length ??
    relatedTemplate?.default_line_items?.length ??
    0;

  const relatedTemplatePageCount =
    relatedTemplate?.template_static_pages?.length ??
    relatedTemplate?.default_static_page_ids?.length ??
    0;

  const createdAt =
    typeof proposal.created_at === "string" && proposal.created_at.trim().length > 0
      ? new Date(proposal.created_at)
      : null;

  const canRegenerate =
    proposal.status === "draft" && !!proposal.pdf_url && !pdf.isGenerating;

  const handleViewPdf = () => {
    const url = proposal.pdf_url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    if (!proposal.client_id) {
      return;
    }

    pdf.generate({
      proposalId,
      clientId: proposal.client_id,
      onSuccess: (generatedUrl) =>
        window.open(generatedUrl, "_blank", "noopener,noreferrer"),
    });
  };

  const handleRegeneratePdf = async () => {
    if (!proposal.client_id) return;
    // Best-effort save — don't block generation if save fails
    if (onBeforeRegenerate) {
      await onBeforeRegenerate().catch(() => {});
    }
    pdf.generate({
      proposalId,
      clientId: proposal.client_id,
      onSuccess: (generatedUrl) =>
        window.open(generatedUrl, "_blank", "noopener,noreferrer"),
    });
  };

  const handleCopySigningLink = async () => {
    if (!signingUrl) return;
    await navigator.clipboard.writeText(signingUrl);
    setCopySuccess("Signing link copied.");
    window.setTimeout(() => setCopySuccess(null), 2000);
  };

  const handleOpenSigningLink = () => {
    if (!signingUrl) return;
    window.open(signingUrl, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async () => {
    await deleteProposal.mutateAsync(proposalId);
    navigate({ to: "/proposal-builder" });
  };

  const handleSendProposal = async () => {
    try {
      setSendError(null);

      const result = await sendProposal.mutateAsync({
        proposalId,
        message: sendMessage.trim() || undefined,
      });

      setLastSendResult({
        provider: result?.provider,
        pdf_url: result?.pdf_url,
        email_sent: result?.email_sent,
        email_skipped_reason: result?.email_skipped_reason,
      });
      setShowSendPanel(false);
      setSendMessage("");
    } catch (err) {
      setSendError(
        err instanceof Error ? err.message : "Failed to send proposal."
      );
    }
  };

  const handleOpenTemplateDetail = () => {
    if (!relatedTemplate?.id) return;

    navigate({
      to: "/proposal-builder/templates/$id",
      params: { id: relatedTemplate.id },
    });
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-8">
          {/* Title row */}
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate({ to: "/proposal-builder" })}
              className="mt-0.5 shrink-0 rounded-lg p-2 text-white/40 transition-colors hover:bg-white/5 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <div className="min-w-0 flex-1">
              <h1 className="truncate text-xl font-bold">
                {proposal.prospect_name}
              </h1>
              <p className="mt-1 text-sm text-white/40">
                {proposal.prospect_city && `${proposal.prospect_city}, `}
                {proposal.prospect_state}
                {(proposal.prospect_city || proposal.prospect_state) && " · "}
                Created{" "}
                {createdAt
                  ? formatDistanceToNow(createdAt, { addSuffix: true })
                  : "recently"}
              </p>
            </div>
          </div>

          {/* Actions row */}
          <div className="mt-4 flex flex-wrap items-center gap-2 pl-10">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm transition-colors hover:bg-white/10"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit proposal
            </button>

            {isBuilderTemplate && relatedTemplate && (
              <button
                type="button"
                onClick={handleOpenTemplateDetail}
                className="flex items-center gap-1.5 rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/10 px-3 py-2 text-sm text-fuchsia-200 transition-colors hover:bg-fuchsia-500/15"
              >
                <Move className="h-3.5 w-3.5" />
                Open builder
              </button>
            )}

            {canSend && (
              <button
                type="button"
                onClick={() => {
                  setSendError(null);
                  setShowSendPanel((current) => !current);
                }}
                className="flex items-center gap-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 transition-colors hover:bg-emerald-500/15"
              >
                <Send className="h-3.5 w-3.5" />
                Send proposal
              </button>
            )}

            {canRegenerate && (
              <button
                type="button"
                onClick={handleRegeneratePdf}
                disabled={pdf.isGenerating}
                title="Re-generate PDF with latest edits"
                className="flex items-center gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-200 transition-colors hover:bg-amber-500/15 disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Regenerate PDF
              </button>
            )}

            <button
              type="button"
              onClick={handleViewPdf}
              disabled={pdf.isGenerating || !proposal.client_id}
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm transition-colors hover:bg-white/10 disabled:opacity-50"
            >
              {pdf.isGenerating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ExternalLink className="h-3.5 w-3.5" />
              )}
              {proposal.pdf_url ? "View PDF" : "Generate PDF"}
            </button>

            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-white/30 transition-colors hover:border-red-500/20 hover:bg-red-500/10 hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-white/40">Delete?</span>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteProposal.isPending}
                  className="flex items-center gap-1 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                >
                  {deleteProposal.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    "Yes, delete"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-lg bg-white/5 px-3 py-1.5 text-xs text-white/40 transition-colors hover:text-white"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* PDF generating banner */}
          {pdf.isGenerating && (
            <div className="mt-3 flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-amber-300">Generating your PDF…</p>
                <p className="text-xs text-amber-400/70">This takes 15–30 seconds. It will open automatically when ready.</p>
              </div>
            </div>
          )}

          {/* PDF generation error */}
          {pdf.error && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-red-300">PDF generation failed</p>
                <p className="mt-0.5 text-xs text-red-400/80">{pdf.error.message}</p>
              </div>
            </div>
          )}
        </div>

        {relatedTemplate && (
          <div className="mb-8 rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-white/30">
              Template handoff
            </p>

            <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-white/85">
                  {relatedTemplate.name}
                </p>
                <p className="mt-1 text-sm leading-6 text-white/45">
                  {relatedTemplate.description?.trim() ||
                    "This proposal was created from a reusable template foundation."}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                    builderCfg.color
                  )}
                >
                  {builderCfg.icon}
                  {builderCfg.label}
                </span>

                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                    fixedCfg.color
                  )}
                >
                  {fixedCfg.icon}
                  {fixedCfg.label}
                </span>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <DetailInfoCard
                icon={<Wrench className="h-4 w-4" />}
                label="Inherited services"
                value={`${relatedTemplateServiceCount}`}
                description="This proposal started from the selected template's service defaults and was then customized for this prospect."
              />
              <DetailInfoCard
                icon={<FileStack className="h-4 w-4" />}
                label="Inherited pages"
                value={`${relatedTemplatePageCount}`}
                description={fixedCfg.description}
              />
            </div>

            {isBuilderTemplate && (
              <div className="mt-4 rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/10 p-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 text-fuchsia-200">
                      <Sparkles className="h-4 w-4" />
                      <p className="text-sm font-semibold">
                        Builder route is now exposed from proposal detail
                      </p>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-white/60">
                      This proposal came from a drag-and-drop template. Use the
                      template builder to adjust reusable pages, sections, and
                      layout structure for future proposals, then come back here
                      to manage this live proposal, PDF, and send/sign flow.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={handleOpenTemplateDetail}
                    className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-fuchsia-400/20 bg-black/20 px-4 py-2 text-sm font-medium text-fuchsia-100 transition-colors hover:bg-black/30"
                  >
                    <Move className="h-4 w-4" />
                    Open template builder
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showSendPanel && (
          <div className="mb-8 rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-white">
                  Send proposal to client
                </p>
                <p className="mt-1 text-sm text-white/45">
                  This is where the send and sign flow begins for the client.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowSendPanel(false);
                  setSendError(null);
                }}
                className="rounded-lg p-2 text-white/35 transition-colors hover:bg-white/5 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.14em] text-white/30">
                  Recipient
                </p>
                <div className="mt-2 flex items-center gap-2 text-sm text-white/80">
                  <Mail className="h-4 w-4 text-white/35" />
                  <span>{proposal.prospect_email || "No client email added"}</span>
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.14em] text-white/30">
                  Proposal status
                </p>
                <p className="mt-2 text-sm text-white/80 capitalize">
                  {proposal.status}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-white/75">
                Optional message
              </label>
              <textarea
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                rows={4}
                placeholder="Add a short note to send with this proposal..."
                className="w-full resize-none rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/20 focus:border-white/20 focus:outline-none"
              />
            </div>

            {!hasProspectEmail && (
              <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
                <p className="text-sm text-amber-200">
                  No client email on file — the proposal will be marked sent but no email will be delivered. You can share the PDF link manually after sending.
                </p>
              </div>
            )}

            {sendError && (
              <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
                <p className="text-sm text-red-300">{sendError}</p>
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowSendPanel(false);
                  setSendError(null);
                }}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/65 transition-colors hover:bg-white/5 hover:text-white"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSendProposal}
                disabled={sendProposal.isPending}
                className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
              >
                {sendProposal.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send now
              </button>
            </div>
          </div>
        )}

        {lastSendResult?.provider === "simple" && lastSendResult.pdf_url && (
          <div className="mb-8 rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-cyan-100">Proposal sent — share PDF link manually</p>
                <p className="mt-0.5 truncate text-xs text-white/40">
                  {lastSendResult.pdf_url}
                </p>
                {lastSendResult.email_skipped_reason && (
                  <p className="mt-1 text-xs text-white/40">{lastSendResult.email_skipped_reason}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (lastSendResult.pdf_url) window.open(lastSendResult.pdf_url, "_blank", "noopener,noreferrer");
                  }}
                  className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/15"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open PDF
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!lastSendResult.pdf_url) return;
                    await navigator.clipboard.writeText(lastSendResult.pdf_url);
                    setCopySuccess("PDF link copied.");
                    window.setTimeout(() => setCopySuccess(null), 2000);
                  }}
                  className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/15"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy link
                </button>
              </div>
            </div>
            {copySuccess && (
              <p className="mt-3 text-xs text-emerald-300">{copySuccess}</p>
            )}
          </div>
        )}

        {!isTerminal ? (
          <div className="mb-8 flex items-center">
            {STATUS_STEPS.map((step, idx) => {
              const completed = idx <= currentStepIdx;
              const current = idx === currentStepIdx;
              const Icon = step.icon;

              return (
                <div key={step.status} className="flex flex-1 items-center">
                  <div className="flex flex-col items-center gap-1.5">
                    <div
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-full border transition-all",
                        completed
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-white/15 text-white/20"
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <span
                      className={cn(
                        "text-[11px] font-medium",
                        current
                          ? "text-white"
                          : completed
                            ? "text-white/50"
                            : "text-white/20"
                      )}
                    >
                      {step.label}
                    </span>
                  </div>

                  {idx < STATUS_STEPS.length - 1 && (
                    <div
                      className={cn(
                        "mx-2 mb-5 h-px flex-1 transition-all",
                        idx < currentStepIdx
                          ? "bg-emerald-500/50"
                          : "bg-white/10"
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className={cn(
              "mb-8 flex items-center gap-3 rounded-xl border px-4 py-3",
              proposal.status === "declined"
                ? "border-red-500/20 bg-red-500/10 text-red-300"
                : "border-white/10 bg-white/5 text-white/50"
            )}
          >
            {proposal.status === "declined" ? (
              <XCircle className="h-5 w-5 shrink-0" />
            ) : (
              <Clock className="h-5 w-5 shrink-0" />
            )}
            <div>
              <p className="text-sm font-medium capitalize">{proposal.status}</p>
              {sigReq?.decline_reason && (
                <p className="mt-0.5 text-xs opacity-75">
                  {sigReq.decline_reason}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mb-8 rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-xs uppercase tracking-[0.14em] text-white/30">
            What happens here
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <DetailInfoCard
              icon={<FileText className="h-4 w-4" />}
              label="Proposal detail"
              value="Review"
              description="Inspect project summary, services, totals, and proposal status in one place."
            />
            <DetailInfoCard
              icon={isBuilderTemplate ? <Move className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
              label={isBuilderTemplate ? "Builder handoff" : "PDF"}
              value={isBuilderTemplate ? "Template route" : "Server-side"}
              description={
                isBuilderTemplate
                  ? "Drag-and-drop templates now have a clear in-app handoff from proposal detail into the template builder route."
                  : "PDF generation happens from the live server-side pipeline, not from the browser."
              }
            />
            <DetailInfoCard
              icon={<Send className="h-4 w-4" />}
              label="Send / sign"
              value="From here"
              description="Client send flow, signing link access, and signed-document tracking all live on this screen."
            />
          </div>
        </div>

        <div className="mb-8 grid grid-cols-2 gap-3">
          {[
            { label: "Sent", ts: proposal.sent_at },
            { label: "Viewed", ts: proposal.viewed_at },
            { label: "Signed", ts: proposal.signed_at },
            { label: "Declined", ts: proposal.declined_at },
          ]
            .filter((d) => d.ts)
            .map((d) => (
              <div
                key={d.label}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
              >
                <p className="mb-1 text-xs text-white/30">{d.label}</p>
                <p className="text-sm font-medium">
                  {format(new Date(d.ts as string), "MMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            ))}
        </div>

        {hasActiveSigningLink && (
          <div className="mb-8 rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">Review & sign link active</p>
                <p className="mt-0.5 truncate text-xs text-white/40">
                  {signingUrl}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={handleOpenSigningLink}
                  className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/15"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open link
                </button>

                <button
                  type="button"
                  onClick={handleCopySigningLink}
                  className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/15"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy link
                </button>
              </div>
            </div>

            {copySuccess && (
              <p className="mt-3 text-xs text-emerald-300">{copySuccess}</p>
            )}
          </div>
        )}

        {proposal.signed_pdf_url && (
          <a
            href={proposal.signed_pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 text-emerald-300 transition-colors hover:bg-emerald-500/15"
          >
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <div>
              <p className="text-sm font-medium">Signed document ready</p>
              <p className="mt-0.5 text-xs opacity-70">
                Click to download the signed PDF
              </p>
            </div>
            <ExternalLink className="ml-auto h-4 w-4 shrink-0 opacity-60" />
          </a>
        )}

        <div className="mb-8 overflow-hidden rounded-xl border border-white/10">
          {(proposal.ai_project_summary || proposal.project_summary) && (
            <div className="border-b border-white/10 px-5 py-4">
              <p className="mb-2 text-xs uppercase tracking-wider text-white/30">
                Project overview
              </p>
              <p className="text-sm leading-relaxed text-white/70">
                {proposal.ai_project_summary ?? proposal.project_summary}
              </p>
            </div>
          )}

          {lineItems.length > 0 && (
            <div>
              <div className="grid grid-cols-12 border-b border-white/10 bg-white/[0.03] px-5 py-2.5">
                <span className="col-span-6 text-[11px] uppercase tracking-wider text-white/30">
                  Service
                </span>
                <span className="col-span-3 text-[11px] uppercase tracking-wider text-white/30">
                  Frequency
                </span>
                <span className="col-span-3 text-right text-[11px] uppercase tracking-wider text-white/30">
                  Price
                </span>
              </div>
              {lineItems.map((item, i) => (
                <LineItemReadRow
                  key={item.id}
                  item={item}
                  isLast={i === lineItems.length - 1}
                />
              ))}
            </div>
          )}

          <div className="space-y-2 border-t border-white/10 px-5 py-4">
            {(proposal.tax_rate ?? 0) > 0 && (
              <>
                <div className="flex justify-between text-sm text-white/40">
                  <span>Subtotal</span>
                  <span>{formatCurrencyValue(proposal.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-white/40">
                  <span>
                    Tax ({((proposal.tax_rate ?? 0) * 100).toFixed(1)}%)
                  </span>
                  <span>{formatCurrencyValue(proposal.tax_amount)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between border-t border-white/10 pt-2 text-base font-bold">
              <span>Total</span>
              <span>{formatCurrencyValue(proposal.total_value)}</span>
            </div>
          </div>
        </div>

        {proposal.notes && (
          <div className="rounded-xl border border-white/10 p-5">
            <p className="mb-2 text-xs uppercase tracking-wider text-white/30">
              Internal notes
            </p>
            <p className="text-sm leading-relaxed text-white/60">
              {proposal.notes}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailInfoCard({
  icon,
  label,
  value,
  description,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 text-white/35">
        {icon}
        <span className="text-xs uppercase tracking-[0.14em]">{label}</span>
      </div>
      <p className="mt-2 text-lg font-semibold text-white/85">{value}</p>
      <p className="mt-2 text-sm leading-6 text-white/40">{description}</p>
    </div>
  );
}

function LineItemReadRow({
  item,
  isLast,
}: {
  item: ProposalLineItem;
  isLast: boolean;
}) {
  const priceUnitLabel = getPriceUnitLabel(item.price_unit);
  const isSqFt = item.price_unit === "per_sqft" && item.square_feet != null;
  const lineTotal = isSqFt
    ? (item.price ?? 0) * item.square_feet!
    : (item.price ?? 0) * (item.quantity ?? 1);

  return (
    <div
      className={cn(
        "grid grid-cols-12 gap-2 px-5 py-3.5",
        !isLast && "border-b border-white/[0.06]"
      )}
    >
      <div className="col-span-6 min-w-0">
        <p className="text-sm font-medium">{item.name}</p>
        {item.description && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-white/35">
            {item.description}
          </p>
        )}
      </div>
      <p className="col-span-3 self-center text-sm text-white/50">
        {item.frequency ?? "—"}
      </p>
      <div className="col-span-3 self-center text-right">
        {isSqFt ? (
          <>
            <p className="text-sm font-semibold text-white/70">
              {formatCurrencyValue(lineTotal)}
            </p>
            <p className="text-xs text-white/30">
              {item.square_feet!.toLocaleString()} sq ft × {formatCurrencyValue(item.price)}/sqft
            </p>
          </>
        ) : (
          <p className="text-sm font-semibold text-white/70">
            {formatCurrencyValue(item.price)}
            {priceUnitLabel ? (
              <span className="ml-1 text-xs font-normal text-white/30">
                / {priceUnitLabel}
              </span>
            ) : null}
          </p>
        )}
      </div>
    </div>
  );
}